<script setup lang="ts">
import { nextTick, onMounted, ref, getCurrentInstance } from 'vue';
import { createObject, deleteObject, listObjects, updateObject } from '@/base/callBackend';
import { CabTheme, uuidOf } from 'common';
import ThemeStrip, {THEME_COLUMNS} from '@/views/admin/ThemeStrip.vue';
import PageHeader from '@/views/ui/PageHeader.vue';
import CardPanel from '@/views/ui/CardPanel.vue';
import RowGrid from '@/views/ui/RowGrid.vue';
import IconButton from '@/views/ui/IconButton.vue';
import {Plus} from 'lucide-vue-next';
import GeneralFilter from '@/views/general/GeneralFilter.vue';
// noinspection ES6UnusedImports - usages in <template> are not properly detected!
import { THEME_CHANGE_EMIT, THEME_REMOVE_EMIT } from '@/base/cabConstants';

defineEmits([THEME_CHANGE_EMIT, THEME_REMOVE_EMIT]);

const themes         = ref<CabTheme[]>([]);
const filteredThemes = ref<CabTheme[]>([]);
const filterActive   = ref<boolean>(false);

onMounted(async () => {
    themes.value = await listObjects(CabTheme);
});

//=====================================================================================
async function onThemeRemove(theme: CabTheme) {
    // the server refuses a thema that still has report items; the row then stays
    if (await deleteObject(theme)) {
        themes.value = themes.value.filter((o) => uuidOf(o) !== uuidOf(theme));
    }
}

async function onThemeAdd() {
    filterActive.value = false;
    const newTheme     = new CabTheme();
    await createObject(newTheme);
    themes.value = [newTheme, ...themes.value];
    await nextTick();
    getThemeStrip(makeStripRef(newTheme))?.firstEdit();
}

async function onThemeChange(theme: CabTheme) {
    const uuid = uuidOf(theme);
    await updateObject(theme);
    themes.value = themes.value.map((o) =>
        uuidOf(o) === uuid ? theme : o,
    );
}

const instance = getCurrentInstance();

function getThemeStrip(ref: string): InstanceType<typeof ThemeStrip> | undefined {
    return (instance?.proxy?.$refs[ref] as any)?.[0];
}

function makeStripRef(x: CabTheme) {
    return `ThemeStrip-${x.uuid}`;
}

</script>

<template>
    <div class="flex flex-col">
        <PageHeader title="Thema's" :context="`${filteredThemes.length} van ${themes.length}`">
            <template #before>
                <IconButton :icon="Plus" label="nieuw thema" variant="primary" data-testid="plus-button" @click="onThemeAdd()"/>
            </template>
            <template #actions>
                <GeneralFilter v-model:active="filterActive" v-model:cab-list="themes" v-model:cab-list-filtered="filteredThemes" :id="'ThemeList'"/>
            </template>
        </PageHeader>
        <CardPanel>
            <RowGrid v-if="filteredThemes.length" :columns="THEME_COLUMNS" header>
                <span class="pl-2">naam</span>
                <span/>
            </RowGrid>
            <div v-else class="px-4 py-6 text-sm text-zzc-muted">geen thema's</div>
            <ThemeStrip
                v-for="theme in filteredThemes"
                :key="uuidOf(theme)"
                :theme="theme"
                :ref="makeStripRef(theme)"
                v-on:[THEME_REMOVE_EMIT]="onThemeRemove"
                v-on:[THEME_CHANGE_EMIT]="onThemeChange"
            />
        </CardPanel>
    </div>
</template>
